import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { savePromoCode, removePromoCode } from '../../actions/cartAction';
import usePromoDiscount from '../../utils/usePromoDiscount';
import useShippingCharge from '../../utils/useShippingCharge';
import { useLang, uiText } from '../../utils/i18n';

const PriceSidebar = ({ cartItems, pricing, walletValueApplied = 0 }) => {

    const dispatch = useDispatch();
    const { promoCode } = useSelector((state) => state.cart);
    const [inputCode, setInputCode] = useState("");
    const { lang } = useLang();

    const originalTotal = cartItems.reduce((sum, item) => sum + (item.cuttedPrice * item.quantity), 0);
    const saleTotal = pricing ? pricing.subtotal : cartItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    const afterOfferTotal = pricing ? pricing.total : saleTotal;

    const promoResult = usePromoDiscount(promoCode, afterOfferTotal);
    const promoDiscount = promoResult?.valid ? promoResult.discountAmount : 0;
    const afterPromoTotal = Math.max(afterOfferTotal - promoDiscount, 0);

    const { charge: shippingCharge, area } = useShippingCharge(afterPromoTotal);
    const finalTotal = afterPromoTotal + shippingCharge;

    const productDiscount = originalTotal - saleTotal;
    const offerDiscount = saleTotal - afterOfferTotal;
    const totalSavings = originalTotal - afterPromoTotal;

    const applyHandler = () => {
        if (!inputCode.trim()) return;
        dispatch(savePromoCode(inputCode.trim().toUpperCase()));
        setInputCode("");
    };

    const removeHandler = () => {
        dispatch(removePromoCode());
    };

    return (
        <div className="flex sticky top-16 sm:h-screen flex-col sm:w-4/12 sm:px-1">

            <div className="flex flex-col bg-white rounded-sm shadow">
                <h1 className="px-6 py-3 border-b font-medium text-gray-500">{uiText('priceDetails', lang)}</h1>

                <div className="flex flex-col gap-2 px-6 py-4 border-b">
                    {!promoCode ? (
                        <div className="flex gap-2">
                            <input
                                type="text"
                                value={inputCode}
                                onChange={(e) => setInputCode(e.target.value)}
                                placeholder={uiText('enterPromoCode', lang)}
                                className="flex-1 border rounded px-3 py-2 text-sm outline-none focus:border-primary-blue"
                            />
                            <button onClick={applyHandler} className="bg-primary-blue text-white text-sm font-medium px-4 py-2 rounded hover:shadow">{uiText('apply', lang)}</button>
                        </div>
                    ) : (
                        <div className="flex justify-between items-start">
                            <div>
                                <span className="font-mono font-medium text-sm bg-gray-100 px-2 py-1 rounded">{promoCode}</span>
                                {promoResult?.valid === false && (
                                    <p className="text-xs text-red-500 mt-1">{promoResult.message}</p>
                                )}
                                {promoResult?.valid && (
                                    <p className="text-xs text-primary-green mt-1">{uiText('appliedSaved', lang)} HK${promoDiscount.toLocaleString()}</p>
                                )}
                            </div>
                            <button onClick={removeHandler} className="text-xs text-red-500 hover:underline">{uiText('remove', lang)}</button>
                        </div>
                    )}
                </div>

                {area && (
                    <div className="flex flex-col gap-1 px-6 py-3 border-b bg-blue-50 text-sm">
                        <p className="font-medium text-gray-700">{uiText('deliveringTo', lang)} {area.name}, {area.region}</p>
                        {area.deliveryDays && <p className="text-gray-500">{uiText('deliveryDaysLabel', lang)} {area.deliveryDays}</p>}
                        {shippingCharge > 0 ? (
                            <p className="text-gray-500">Add HK${(area.minOrderValue - afterPromoTotal).toLocaleString()} {uiText('addMoreForFreeShipping', lang)}</p>
                        ) : (
                            <p className="text-primary-green">{uiText('qualifyFreeShipping', lang)}</p>
                        )}
                    </div>
                )}

                <div className="flex flex-col gap-4 p-6 pb-3">
                    <p className="flex justify-between">{uiText('mrpLabel', lang)} ({cartItems.length} {uiText('itemsWord', lang)}) <span>HK${originalTotal.toLocaleString()}</span></p>
                    <p className="flex justify-between">{uiText('productDiscount', lang)} <span className="text-primary-green">- HK${productDiscount.toLocaleString()}</span></p>
                    <p className="flex justify-between">{uiText('salePrice', lang)} <span>HK${saleTotal.toLocaleString()}</span></p>
                    {offerDiscount > 0 && (
                        <p className="flex justify-between">{uiText('offerDiscountLabel', lang)} <span className="text-primary-green">- HK${offerDiscount.toLocaleString()}</span></p>
                    )}
                    {promoDiscount > 0 && (
                        <p className="flex justify-between">{uiText('promoDiscountLabel', lang)} <span className="text-primary-green">- HK${promoDiscount.toLocaleString()}</span></p>
                    )}
                    <p className="flex justify-between">
                        {uiText('shippingChargeLabel', lang)}
                        {area ? (
                            shippingCharge > 0
                                ? <span>HK${shippingCharge.toLocaleString()}</span>
                                : <span className="text-primary-green">{uiText('freeLabel', lang)}</span>
                        ) : (
                            <span className="text-gray-400">{uiText('calculatedAtCheckout', lang)}</span>
                        )}
                    </p>

                    {walletValueApplied > 0 && (
                        <p className="flex justify-between text-primary-orange">{uiText('walletAppliedLabel', lang)} <span>- HK${walletValueApplied.toLocaleString()}</span></p>
                    )}

                    <div className="border border-dashed"></div>
                    <p className="flex justify-between text-lg font-medium">{uiText('totalAmountLabel', lang)} <span>HK${Math.max(finalTotal - walletValueApplied, 0).toLocaleString()}</span></p>
                    <div className="border border-dashed"></div>

                    <p className="font-medium text-primary-green">{uiText('youWillSave', lang)} HK${totalSavings.toLocaleString()} {uiText('onThisOrder', lang)}</p>

                </div>

            </div>

        </div>
    );
};

export default PriceSidebar;
